// 168B — `react/react-in-jsx-scope` counts PER JSX ELEMENT, and 174 §6.2 adds
// two to this file. The import IS the rule's own remedy, and it clears all 130.
import React, { useEffect, useMemo, useState } from "react";
import type { Dispatch } from "react"; // 190 §E.2
import {
  Copy,
  Trash2,
  Save,
  RotateCcw,
  Search,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import type { TFn } from "../lib/taxonomyTypes"; // 190 §E.2 — `t` narrowed at the switchboard's seam
import { CT, RAD, FS } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import MenuSelect from "./MenuSelect"; // 154B §2
import { useConfirm } from "./useConfirmAndUndo";
import Table from "./Table";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// Phase M+3.7 — pick the active-language displayLabel from a Role row.
// Falls back displayLabelXX → displayLabelEn → displayLabel → name.
function pickRoleLabel(r: any) {
  if (!r) return "";
  const lang = (typeof window !== "undefined" && window.__lxLang) || "en";
  if (lang === "fr" && r.displayLabelFr) return r.displayLabelFr;
  if (lang === "nl" && r.displayLabelNl) return r.displayLabelNl;
  return r.displayLabelEn || r.displayLabel || r.name;
}

// Phase M+2c — Settings → Users & Roles → Roles & Permissions admin UI.
//
// Two views: per-role editor and section-defaults matrix. Both consume the
// admin-only /api/roles CRUD that shipped in M+2b.
//
// Phase 37 (pieces 1 + 3) — grouping is now driven by the SERVER section catalog
// (GET /api/permissions/catalog): every key carries view + subsection, ordered
// by the SUBSECTIONS catalog (a single canonical source in
// server/src/lib/sectionRegistry.js). This replaces the hand-coded bucketFor
// prefix rules. CaseView / CollectiveCase / Lateral are now grouped into
// subsections that mirror the app's section structure, in BOTH tabs, and every
// subsection is individually collapsible (collapsed by default).
const VIEW_ORDER = ["CaseView", "CollectiveCaseView", "Lateral", "Settings"];
const VIEW_LABEL: Record<string, string> = {
  CaseView: "Case view",
  CollectiveCaseView: "Collective case",
  Lateral: "Lateral / sidebar",
  Settings: "Settings",
};
function viewColor(v: any) {
  if (v === "CaseView") return "rgba(74,124,255,.15)";
  if (v === "CollectiveCaseView") return "rgba(168,85,247,.15)";
  if (v === "Lateral") return "rgba(34,197,94,.12)";
  return "rgba(234,179,8,.14)"; // Settings
}

// Build ordered groups [{ view, subId, subLabel, subLabelKey, items:[meta...] }]
// from the fetched catalog + the live key universe (role.defaults keys). Iterates
// catalog.sections (registry order: quad bits in v/c/e/d order, then ops) so the
// keys within each subsection are stably ordered, then walks catalog.subsections
// for the group order.
function buildGroups(catalog: any, keyUniverse: any) {
  if (!catalog) {
    // Catalog unavailable (endpoint failed) — degrade to a single flat group so
    // the full key set still renders rather than an empty grid.
    const keys = [...keyUniverse].sort();
    return keys.length
      ? [
          {
            view: "All",
            subId: "_all",
            subLabel: "All permissions",
            subLabelKey: "permSubsection._all",
            items: keys.map((k) => ({ key: k, displayLabel: k, label: k })),
          },
        ]
      : [];
  }
  const bySub: Record<string, any> = {};
  for (const m of catalog.sections) {
    if (!keyUniverse.has(m.key)) continue;
    (bySub[m.subsection || "_misc"] ||= []).push(m);
  }
  const groups = [];
  for (const ss of catalog.subsections) {
    const items = bySub[ss.id];
    if (items && items.length) {
      groups.push({
        view: ss.view,
        subId: ss.id,
        subLabel: ss.label,
        subLabelKey: ss.labelKey,
        items,
      });
    }
  }
  // Any keys whose subsection isn't in the catalog (defensive) land in a misc
  // bucket per view so nothing silently vanishes.
  if (bySub["_misc"]) {
    groups.push({
      view: "Settings",
      subId: "_misc",
      subLabel: "Other",
      subLabelKey: "permSubsection._misc",
      items: bySub["_misc"],
    });
  }
  return groups;
}

// Toggle pill — same visual vocabulary as TeamAccessPanel
function PermPill({ on, onClick, disabled }: { on?: any; onClick?: any; disabled?: any }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: FS.s9_5,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: RAD.r10,
        background: on ? "rgba(34,197,94,.18)" : "rgba(245,158,11,.12)",
        color: on ? "var(--gn)" : "var(--am)",
        border: `1px solid ${on ? "rgba(34,197,94,.35)" : "rgba(245,158,11,.30)"}`,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
      }}
    >
      {on
        ? t("roles.allow_pill", { fallback: "✓ allow" })
        : t("roles.deny_pill", { fallback: "✗ deny" })}
    </button>
  );
}

export default function RolesAdminPanel({ toast, isAdmin }: { toast?: any; isAdmin?: any }) {
  const { t } = useTranslation();
  const askConfirm = useConfirm();
  const [tab, setTab] = useState("per-role"); // 'per-role' | 'matrix'
  const [roles, setRoles] = useState<any[]>([]);
  const [secCatalog, setSecCatalog] = useState<any>(null); // { sections, subsections }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<any>(null);
  const [selectedId, setSelectedId] = useState<any>(null);
  const [draft, setDraft] = useState<any>(null); // dirty copy of selected role
  const [saving, setSaving] = useState(false);
  const [matrixBusyKey, setMatrixBusyKey] = useState<any>(null); // `${roleId}:${sectionKey}` while saving
  // Phase 37 — filter chips are now per-VIEW (4) instead of per-bucket (11);
  // subsections within a view are individually collapsible.
  const [viewFilter, setViewFilter] = useState(() =>
    Object.fromEntries(VIEW_ORDER.map((v) => [v, true])),
  );
  const [matrixQuery, setMatrixQuery] = useState("");
  const [duplicateModal, setDuplicateModal] = useState<any>(null); // { sourceRole }

  // 163 §B.3 — the fields this panel actually reads off a /roles row.
  type RoleRow = { id: number; name?: string; displayLabel?: string };

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      const [rows, cat] = await Promise.all([
        api<RoleRow[]>("/roles"),
        api<{ sections?: unknown[] } | null>("/permissions/catalog").catch(
          () => null,
        ),
      ]);
      setRoles(rows);
      if (cat && Array.isArray(cat.sections)) setSecCatalog(cat);
      if (selectedId == null && rows.length > 0) setSelectedId(rows[0].id);
    } catch (e: any) {
      setErr(
        e.message ||
          t("roles.failed_to_load", { fallback: "Failed to load roles" }),
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    // 163 §B.1 — the admin guard now lives BELOW the hooks (rules of hooks);
    // this effect keeps its old behaviour by not loading for non-admins.
    if (!isAdmin) return;
    reload(); /* eslint-disable-next-line */
  }, [isAdmin]);

  const selected = useMemo(
    () => roles.find((r) => r.id === selectedId) || null,
    [roles, selectedId],
  );

  // Live key universe: every key present across role defaults (the server pads
  // each role to the full SECTION_KEYS, so this is the complete set).
  const keyUniverse = useMemo(() => {
    const s = new Set();
    for (const r of roles || [])
      for (const k of Object.keys(r.defaults || {})) s.add(k);
    return s;
  }, [roles]);

  // Ordered, catalog-driven groups (view → subsection → keys).
  const groups = useMemo(
    () => buildGroups(secCatalog, keyUniverse),
    [secCatalog, keyUniverse],
  );

  // Hydrate the draft when the selected role changes (and discard any pending edits).
  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft({
      id: selected.id,
      name: selected.name,
      displayLabel: selected.displayLabel || "",
      displayLabelEn: selected.displayLabelEn || selected.displayLabel || "",
      displayLabelFr: selected.displayLabelFr || "",
      displayLabelNl: selected.displayLabelNl || "",
      rateRatio: selected.rateRatio == null ? "" : String(selected.rateRatio),
      isFeeEarner: !!selected.isFeeEarner,
      billsAtZero: !!selected.billsAtZero,
      defaultRatio:
        selected.defaultRatio == null ? "100" : String(selected.defaultRatio),
      defaults: { ...(selected.defaults || {}) },
      isSystem: !!selected.isSystem,
      isPreset: !!selected.isPreset,
    });
  }, [selectedId, roles]);

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    if (
      draft.displayLabelEn !==
      (selected.displayLabelEn || selected.displayLabel || "")
    )
      return true;
    if (draft.displayLabelFr !== (selected.displayLabelFr || "")) return true;
    if (draft.displayLabelNl !== (selected.displayLabelNl || "")) return true;
    const origRatio =
      selected.rateRatio == null ? "" : String(selected.rateRatio);
    if (draft.rateRatio !== origRatio) return true;
    if (draft.isFeeEarner !== !!selected.isFeeEarner) return true;
    if (draft.billsAtZero !== !!selected.billsAtZero) return true;
    const origDefRatio =
      selected.defaultRatio == null ? "100" : String(selected.defaultRatio);
    if (draft.defaultRatio !== origDefRatio) return true;
    const origDef = selected.defaults || {};
    for (const k of Object.keys(draft.defaults)) {
      if (!!draft.defaults[k] !== !!origDef[k]) return true;
    }
    return false;
  }, [selected, draft]);

  // 163 §B.1 — the guard moved below the hooks; the hooks moved above the guard.
  if (!isAdmin) return null;

  async function saveDraft() {
    if (!draft || !dirty) return;
    setSaving(true);
    try {
      const body = {
        displayLabelEn: draft.displayLabelEn || draft.displayLabel,
        displayLabelFr: draft.displayLabelFr || null,
        displayLabelNl: draft.displayLabelNl || null,
        rateRatio: draft.rateRatio === "" ? null : Number(draft.rateRatio),
        isFeeEarner: draft.isFeeEarner,
        billsAtZero: draft.billsAtZero,
        defaultRatio:
          draft.defaultRatio === ""
            ? 100
            : Math.max(0, Math.trunc(Number(draft.defaultRatio) || 0)),
        defaults: draft.defaults,
      };
      const updated = await api<RoleRow>(`/roles/${draft.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      setRoles((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      toast &&
        toast(
          t("roles.saved_named", {
            name: updated.displayLabel || updated.name,
            fallback: `Saved ${updated.displayLabel || updated.name}`,
          }),
        );
    } catch (e: any) {
      toast &&
        toast(
          "⚠ " +
            (e.message || t("common.save_failed", { fallback: "Save failed" })),
        );
    }
    setSaving(false);
  }

  function discardDraft() {
    if (!selected) return;
    setDraft({
      id: selected.id,
      name: selected.name,
      displayLabel: selected.displayLabel || "",
      displayLabelEn: selected.displayLabelEn || selected.displayLabel || "",
      displayLabelFr: selected.displayLabelFr || "",
      displayLabelNl: selected.displayLabelNl || "",
      rateRatio: selected.rateRatio == null ? "" : String(selected.rateRatio),
      isFeeEarner: !!selected.isFeeEarner,
      billsAtZero: !!selected.billsAtZero,
      defaultRatio:
        selected.defaultRatio == null ? "100" : String(selected.defaultRatio),
      defaults: { ...(selected.defaults || {}) },
      isSystem: !!selected.isSystem,
      isPreset: !!selected.isPreset,
    });
  }

  async function deleteRole(role: any) {
    if (role.isSystem) {
      toast &&
        toast(
          t("roles.system_cannot_delete", {
            fallback: "⚠ System roles cannot be deleted",
          }),
        );
      return;
    }
    askConfirm({
      // 161C §3 — the role is named, so the title names it.
      title: t("confirm.delete_role_title", {
        name: role.displayLabel || role.name,
        fallback: `Delete ${role.displayLabel || role.name}?`,
      }),
      message: t("roles.confirm_delete", {
        name: role.displayLabel || role.name,
        fallback: `Delete role "${role.displayLabel || role.name}"? This cannot be undone.`,
      }),
      confirmLabel: t("common.delete", { fallback: "Delete" }),
      onConfirm: async () => {
        try {
          await api(`/roles/${role.id}`, { method: "DELETE" });
          setRoles((rs) => rs.filter((r) => r.id !== role.id));
          if (selectedId === role.id)
            setSelectedId(roles.find((r) => r.id !== role.id)?.id || null);
          toast && toast(t("roles.role_deleted", { fallback: "Role deleted" }));
        } catch (e: any) {
          toast &&
            toast(
              "⚠ " +
                (e.message ||
                  t("roles.delete_failed", { fallback: "Delete failed" })),
            );
        }
      },
    });
  }

  async function doDuplicate(sourceRole: any, name: any, displayLabel: any) {
    try {
      const created = await api<RoleRow>(`/roles/${sourceRole.id}/duplicate`, {
        method: "POST",
        body: JSON.stringify({ name, displayLabel }),
      });
      setRoles((rs) => [...rs, created]);
      setSelectedId(created.id);
      setTab("per-role");
      toast &&
        toast(
          t("roles.created_named", {
            name: created.displayLabel,
            fallback: `Created ${created.displayLabel}`,
          }),
        );
      setDuplicateModal(null);
    } catch (e: any) {
      toast &&
        toast(
          "⚠ " +
            (e.message ||
              t("roles.duplicate_failed", { fallback: "Duplicate failed" })),
        );
    }
  }

  // Optimistic toggle in the matrix view. Each cell sends a one-key PUT.
  async function toggleMatrixCell(role: any, sectionKey: any) {
    const newDefaults = {
      ...(role.defaults || {}),
      [sectionKey]: !role.defaults?.[sectionKey],
    };
    const busyKey = `${role.id}:${sectionKey}`;
    setMatrixBusyKey(busyKey);
    setRoles((rs) =>
      rs.map((r) => (r.id === role.id ? { ...r, defaults: newDefaults } : r)),
    );
    try {
      const updated = await api<RoleRow>(`/roles/${role.id}`, {
        method: "PUT",
        body: JSON.stringify({ defaults: newDefaults }),
      });
      setRoles((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
    } catch (e: any) {
      // Rollback
      setRoles((rs) =>
        rs.map((r) =>
          r.id === role.id ? { ...r, defaults: role.defaults || {} } : r,
        ),
      );
      toast &&
        toast(
          "⚠ " +
            (e.message || t("common.save_failed", { fallback: "Save failed" })),
        );
    }
    setMatrixBusyKey(null);
  }

  if (loading)
    return (
      <p style={{ fontSize: FS.s11, color: "var(--tm)", padding: "6px 0" }}>
        {t("roles.loading", { fallback: "Loading roles…" })}
      </p>
    );
  if (err)
    return (
      <p style={{ fontSize: FS.s11, color: "var(--rd)", padding: "6px 0" }}>
        ⚠ {err}
      </p>
    );

  // Matrix filter: keep groups whose view is enabled and (when a query is set)
  // that still have at least one matching key. The query filters keys within
  // each group; empty groups are dropped.
  const q = matrixQuery.trim().toLowerCase();
  const filteredGroups = groups
    .filter((g) => viewFilter[g.view])
    .map((g) => {
      if (!q) return g;
      const items = g.items.filter(
        (m: any) =>
          m.key.toLowerCase().includes(q) ||
          (m.displayLabel || m.label || "").toLowerCase().includes(q),
      );
      return { ...g, items };
    })
    .filter((g) => g.items.length > 0);
  const filteredKeyCount = filteredGroups.reduce(
    (n, g) => n + g.items.length,
    0,
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--br)",
          paddingBottom: 4,
        }}
      >
        {[
          [
            "per-role",
            t("roles.tab_per_role", { fallback: "Per-role editor" }),
          ],
          [
            "matrix",
            t("roles.tab_matrix", { fallback: "Section-defaults matrix" }),
          ],
          /* 190 §E.2 — THE SWITCHBOARD: FirmSettings.hiddenSections. */
          [
            "hidden",
            (t as TFn)("roles.tab_hidden", { fallback: "Hidden sections (product shape)" }),
          ],
        ].map(([k, l]) => (
          /* 188 (7) — STANDALONE r6 PILLS, left deliberately: each button
             bordered on its own (`--ac` when on), transparent when off, under
             a rule. SegmentedControl is a joined pill and this is not one;
             S3c's button pass takes these as buttons. No later pass reads
             "tab" here and converts it. Measured in docs/188-switcher-strips.md. */
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            style={{
              padding: "4px 10px",
              fontSize: FS.s11,
              fontWeight: 600,
              background: tab === k ? "var(--as)" : "transparent",
              color: tab === k ? "var(--ac)" : "var(--tm)",
              border: "1px solid " + (tab === k ? "var(--ac)" : "var(--br)"),
              borderRadius: RAD.r6,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "per-role" && (
        <PerRoleEditor
          roles={roles}
          groups={groups}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          draft={draft}
          setDraft={setDraft}
          dirty={dirty}
          saving={saving}
          onSave={saveDraft}
          onDiscard={discardDraft}
          onDelete={deleteRole}
          onAskDuplicate={(role: any) => setDuplicateModal({ sourceRole: role })}
        />
      )}

      {tab === "hidden" && <HiddenSectionsView groups={groups} toast={toast as Dispatch<string> | undefined} />}

      {tab === "matrix" && (
        <MatrixView
          roles={roles}
          groups={filteredGroups}
          keyCount={filteredKeyCount}
          query={matrixQuery}
          setQuery={setMatrixQuery}
          viewFilter={viewFilter}
          setViewFilter={setViewFilter}
          busyKey={matrixBusyKey}
          onToggle={toggleMatrixCell}
          onAskDuplicate={(role: any) => setDuplicateModal({ sourceRole: role })}
        />
      )}

      {duplicateModal && (
        <DuplicateModal
          source={duplicateModal.sourceRole}
          existingNames={roles.map((r) => r.name)}
          onCancel={() => setDuplicateModal(null)}
          onConfirm={(name: any, label: any) =>
            doDuplicate(duplicateModal.sourceRole, name, label)
          }
        />
      )}
    </div>
  );
}

// Section-label helper shared by both tabs: prefer the i18n section.<key> string,
// fall back to the catalog displayLabel.
function useSectionLabel() {
  const { t } = useTranslation();
  return (m: any) =>
    t(`section.${m.key}`, { fallback: m.displayLabel || m.label || m.key });
}

// A view → subsection collapsible group, rendered in both tabs. `renderItem`
// draws the per-key row body (a pill in the per-role editor, role cells in the
// matrix). Collapsed by default (Phase 37 piece 3).
function ViewSubsectionGroups({ groups, open, onToggle, renderHeaderMeta, renderItem }: { groups?: any; open?: any; onToggle?: any; renderHeaderMeta?: any; renderItem?: any }) {
  const { t } = useTranslation();
  const subLabel = (g: any) => t(g.subLabelKey, { fallback: g.subLabel });
  let lastView = null;
  const out = [];
  for (const g of groups) {
    if (g.view !== lastView) {
      lastView = g.view;
      out.push(
        <div
          key={`vh:${g.view}`}
          style={{
            fontSize: FS.s9_5,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".5px",
            color: "var(--ts)",
            margin: "8px 2px 2px",
          }}
        >
          {t(`permView.${g.view}`, { fallback: VIEW_LABEL[g.view] || g.view })}
        </div>,
      );
    }
    const isOpen = !!open[g.subId];
    out.push(
      <div
        key={g.subId}
        style={{
          border: "1px solid var(--br)",
          borderRadius: RAD.r8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            background: viewColor(g.view),
            cursor: "pointer",
          }}
          onClick={() => onToggle(g.subId)}
        >
          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <span style={{ fontSize: FS.s11, fontWeight: 600, color: "var(--tp)" }}>
            {subLabel(g)}
          </span>
          {renderHeaderMeta && renderHeaderMeta(g)}
        </div>
        {isOpen && <div>{g.items.map((m: any) => renderItem(m))}</div>}
      </div>,
    );
  }
  return <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{out}</div>;
}

function PerRoleEditor({
  roles,
  groups,
  selectedId,
  setSelectedId,
  draft,
  setDraft,
  dirty,
  saving,
  onSave,
  onDiscard,
  onDelete,
  onAskDuplicate,
}: any) {
  const { t } = useTranslation();
  const sectionLabel = useSectionLabel();
  // Phase 37 — collapse keyed by subsection id, collapsed by default.
  const [openSubs, setOpenSubs] = useState<Record<string, any>>({});
  const selected = roles.find((r: any) => r.id === selectedId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Role picker row */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <label
          style={{
            fontSize: FS.s10,
            color: "var(--tm)",
            textTransform: "uppercase",
            letterSpacing: ".4px",
            fontWeight: 600,
          }}
        >
          {t("common.role", { fallback: "Role" })}
        </label>
        {/* 154B §2 — the option's three JSX children were adjacent text nodes,
            so the label is their concatenation and nothing else changes. */}
        <MenuSelect
          className="field-input"
          style={{ minWidth: 220, padding: "3px 6px", fontSize: FS.s11 }}
          value={selectedId ? String(selectedId) : ""}
          onChange={(v) => setSelectedId(+v)}
          options={roles.map(
            (r: any) =>
              [
                String(r.id),
                pickRoleLabel(r) +
                  (r.isSystem
                    ? " " + t("roles.tag_system", { fallback: "(system)" })
                    : "") +
                  (r.isPreset
                    ? " " + t("roles.tag_preset", { fallback: "(preset)" })
                    : ""),
              ] as const,
          )}
        />
        {selected && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{
              padding: "3px 8px",
              fontSize: FS.s10,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
            onClick={() => onAskDuplicate(selected)}
          >
            <Copy size={10} />
            {t("roles.duplicate", { fallback: "Duplicate" })}
          </button>
        )}
        {selected && (
          <button
            type="button"
            disabled={selected.isSystem}
            className="btn btn-danger"
            title={
              selected.isSystem
                ? t("roles.btn.system_role_no_delete_tooltip", {
                    fallback: "System roles cannot be deleted",
                  })
                : undefined
            }
            style={{
              padding: "3px 8px",
              fontSize: FS.s10,
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              ...(selected.isSystem
                ? { opacity: 0.5, cursor: "not-allowed" }
                : null),
            }}
            onClick={() => onDelete(selected)}
          >
            <Trash2 size={10} />
            {t("common.delete", { fallback: "Delete" })}
          </button>
        )}
      </div>

      {!selected || !draft ? (
        <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
          {t("roles.no_role_selected", { fallback: "No role selected." })}
        </p>
      ) : (
        <>
          {/* Metadata */}
          <div
            style={{
              border: "1px solid var(--br)",
              borderRadius: RAD.r8,
              padding: "10px 12px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 10,
              background: "var(--bi)",
            }}
          >
            <div className="field" style={{ margin: 0 }}>
              <label>
                {t("roles.internal_name", { fallback: "Internal name" })}
              </label>
              <input autoComplete="off" /* 207 §18 */
                className="field-input no-contact-autofill"
                value={draft.name}
                disabled
                title={
                  selected.isSystem
                    ? t("roles.system_cannot_rename", {
                        fallback: "System roles cannot be renamed",
                      })
                    : t("roles.internal_name_immutable", {
                        fallback: "Internal name is immutable from this UI",
                      })
                }
                style={{
                  fontFamily: "'JetBrains Mono',monospace",
                  fontSize: FS.s10_5,
                }}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>
                {t("roles.display_label_en", {
                  fallback: "Display label (EN)",
                })}
              </label>
              <input
                className="field-input"
                value={draft.displayLabelEn}
                onChange={(e) =>
                  setDraft((d: any) => ({
                    ...d,
                    displayLabelEn: e.target.value,
                    displayLabel: e.target.value,
                  }))
                }
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>
                {t("roles.display_label_fr", {
                  fallback: "Display label (FR)",
                })}
              </label>
              <input
                className="field-input"
                value={draft.displayLabelFr}
                onChange={(e) =>
                  setDraft((d: any) => ({ ...d, displayLabelFr: e.target.value }))
                }
                placeholder={t("roles.falls_back_en_placeholder", {
                  fallback: "(falls back to EN if blank)",
                })}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>
                {t("roles.display_label_nl", {
                  fallback: "Display label (NL)",
                })}
              </label>
              <input
                className="field-input"
                value={draft.displayLabelNl}
                onChange={(e) =>
                  setDraft((d: any) => ({ ...d, displayLabelNl: e.target.value }))
                }
                placeholder={t("roles.falls_back_en_placeholder", {
                  fallback: "(falls back to EN if blank)",
                })}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>
                {t("roles.rate_ratio", { fallback: "Rate ratio (0–1)" })}
              </label>
              <input
                className="field-input"
                type="number"
                step="0.0001"
                min="0"
                max="1"
                value={draft.rateRatio}
                onChange={(e) =>
                  setDraft((d: any) => ({ ...d, rateRatio: e.target.value }))
                }
                placeholder="e.g. 1.0000"
              />
            </div>
            {/* Phase auth-c2 Step 6 — per-role default billing ratio
                (% of case referenceHourlyRate). Replaces the hardcoded
                ROLE_RATIO map. */}
            <div className="field" style={{ margin: 0 }}>
              <label>
                {t("roles.default_ratio", {
                  fallback: "Default billing ratio (%)",
                })}
              </label>
              <input
                className="field-input"
                type="number"
                step="1"
                min="0"
                max="200"
                value={draft.defaultRatio}
                onChange={(e) =>
                  setDraft((d: any) => ({ ...d, defaultRatio: e.target.value }))
                }
                placeholder="e.g. 100"
              />
            </div>
            {/* Phase 24 (C2) — firmWideVisibility control removed; cross-case
                visibility is now per-SURFACE (Surface-scope editor), not a
                per-role flag. */}
            {/* Phase auth-c2 — billing attributes (replace hardcoded role
                name reads in cost report + effective-rate). */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s11,
                cursor: "pointer",
                alignSelf: "end",
                paddingBottom: 4,
              }}
            >
              <input
                type="checkbox"
                checked={draft.isFeeEarner}
                onChange={(e) =>
                  setDraft((d: any) => ({ ...d, isFeeEarner: e.target.checked }))
                }
              />
              <span>
                {t("roles.is_fee_earner", {
                  fallback:
                    "Fee earner (time counted as firm fees in reports)",
                })}
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: FS.s11,
                cursor: "pointer",
                alignSelf: "end",
                paddingBottom: 4,
              }}
            >
              <input
                type="checkbox"
                checked={draft.billsAtZero}
                onChange={(e) =>
                  setDraft((d: any) => ({ ...d, billsAtZero: e.target.checked }))
                }
              />
              <span>
                {t("roles.bills_at_zero", {
                  fallback: "Bills at €0 (time entries non-billable)",
                })}
              </span>
            </label>
            {selected.isSystem && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: FS.s9_5,
                    fontWeight: 700,
                    padding: "2px 7px",
                    borderRadius: RAD.r10,
                    background: "rgba(168,85,247,.15)",
                    color: CT.c_c084fc,
                    border: "1px solid rgba(168,85,247,.35)",
                  }}
                >
                  {t("roles.system_preset_badge", {
                    fallback: "SYSTEM PRESET",
                  })}
                </span>
                <span style={{ fontSize: FS.s10, color: "var(--tm)" }}>
                  {t("roles.system_preset_help", {
                    fallback:
                      "Defaults / display label / ratio / visibility editable. Name immutable. Cannot be deleted.",
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Defaults grouped by view → subsection (collapsed by default) */}
          <ViewSubsectionGroups
            groups={groups}
            open={openSubs}
            onToggle={(id: any) => setOpenSubs((p) => ({ ...p, [id]: !p[id] }))}
            renderHeaderMeta={(g: any) => (
              <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
                {t("roles.allowed_count", withCount(g.items.filter((m: any) => draft.defaults[m.key]).length, "allowed", { total: g.items.length,
                  fallback: `${g.items.filter((m: any) => draft.defaults[m.key]).length} / ${g.items.length} allowed`,
                }))}
              </span>
            )}
            renderItem={(m: any) => (
              <div
                key={m.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "5px 10px",
                  borderTop: "1px solid var(--br)",
                  fontSize: FS.s11,
                }}
              >
                <span style={{ flex: 1, color: "var(--tp)" }}>
                  {sectionLabel(m)}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: FS.s9,
                    color: "var(--tm)",
                  }}
                >
                  {m.key}
                </span>
                <PermPill
                  on={!!draft.defaults[m.key]}
                  onClick={() =>
                    setDraft((d: any) => ({
                      ...d,
                      defaults: {
                        ...d.defaults,
                        [m.key]: !d.defaults[m.key],
                      },
                    }))
                  }
                />
              </div>
            )}
          />

          {/* Action bar */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              padding: "8px 0",
              borderTop: "1px solid var(--br)",
            }}
          >
            <span
              style={{ fontSize: FS.s10, color: dirty ? "var(--am)" : "var(--tm)" }}
            >
              {dirty
                ? t("roles.unsaved_changes", { fallback: "● Unsaved changes" })
                : t("roles.all_saved", { fallback: "✓ All changes saved" })}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              style={{
                padding: "4px 12px",
                fontSize: FS.s11,
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
              disabled={!dirty || saving}
              onClick={onDiscard}
            >
              <RotateCcw size={11} />
              {t("roles.discard", { fallback: "Discard" })}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              style={{
                padding: "4px 12px",
                fontSize: FS.s11,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
              disabled={!dirty || saving}
              onClick={onSave}
            >
              <Save size={11} />
              {saving
                ? t("roles.saving", { fallback: "Saving…" })
                : t("roles.save_changes", { fallback: "Save changes" })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* 190 §E.2 — THE SWITCHBOARD (Laurent's (c)). Every registry key with a tick,
   grouped exactly as the two tabs above group them (the same catalog, the
   same collapsible subsections), writing FirmSettings.hiddenSections through
   PUT /firm-settings/hidden-sections (admin only — this panel already sits
   behind settings.users.rolesPerm). The screen says the rule the plane
   enforces (lib/sectionVisibility.js: "HIDDEN BEATS ADMIN"): a ticked key is
   gone for the firm, its administrator included — product shape, not
   authorization — and can() answers false for it before the admin overlay,
   so a hidden section has no dead affordance anywhere and the API refuses
   it. Reads `{ hiddenSections }`, writes the whole list on each tick (the
   server validates every key against the registry). */
interface HiddenSectionsViewProps {
  readonly groups: readonly { view: string; subId: string; items: { key: string }[] }[];
  readonly toast?: Dispatch<string>;
}
function HiddenSectionsView({ groups, toast }: HiddenSectionsViewProps) {
  const { t } = useTranslation() as { t: TFn };
  const sectionLabel = useSectionLabel();
  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({});
  const [hidden, setHidden] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const notify = toast;
  useEffect(() => {
    api<{ hiddenSections?: string[] }>("/firm-settings/hidden-sections")
      .then((r) => { setHidden(new Set(Array.isArray(r.hiddenSections) ? r.hiddenSections : [])); })
      .catch(() => { setHidden(new Set()); });
  }, []);
  const write = (next: Set<string>) => {
    setBusy(true);
    api<{ hiddenSections?: string[] }>("/firm-settings/hidden-sections", { method: "PUT", body: JSON.stringify({ hiddenSections: [...next].sort((a, b) => a.localeCompare(b)) }) })
      .then((r) => { setHidden(new Set(Array.isArray(r.hiddenSections) ? r.hiddenSections : [])); })
      .catch((e: unknown) => { notify?.(t("roles.hidden_save_failed", { fallback: "Could not save the hidden sections" }) + ": " + (e instanceof Error ? e.message : String(e))); })
      .finally(() => { setBusy(false); });
  };
  const toggle = (key: string) => {
    if (!hidden) return;
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key); else next.add(key);
    write(next);
  };
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div
        style={{
          fontSize: FS.s11,
          color: "var(--tp)",
          padding: "8px 10px",
          border: "1px solid var(--br)",
          borderRadius: RAD.r8,
          background: "var(--bg2)",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          {t("roles.hidden_rule_title", { fallback: "Hiding beats admin." })}
        </div>
        {t("roles.hidden_rule", {
          fallback:
            "A ticked section is switched off for the whole firm — it is gone for the administrator too, deliberately. This is the firm's product shape, not a permission: the roles matrix decides who among the firm reaches a section that exists; this list decides whether it exists at all. The API refuses a hidden section for everyone.",
        })}
        <div style={{ marginTop: 4, color: "var(--tm)" }}>
          {t("roles.hidden_count", {
            hidden: hidden ? hidden.size : 0,
            count: hidden ? hidden.size : 0, // 208 §20
            total,
            fallback: `${String(hidden ? hidden.size : 0)} of ${String(total)} sections hidden`,
          })}
        </div>
      </div>
      {hidden === null ? (
        <div style={{ fontSize: FS.s11, color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</div>
      ) : (
        <ViewSubsectionGroups
          groups={groups}
          open={openSubs}
          onToggle={(id: string) => { setOpenSubs((p) => ({ ...p, [id]: !p[id] })); }}
          renderHeaderMeta={(g: { items: { key: string }[] }) => (
            <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
              {t("roles.hidden_count", {
                hidden: g.items.filter((m) => hidden.has(m.key)).length,
                count: g.items.filter((m) => hidden.has(m.key)).length, // 208 §20
                total: g.items.length,
                fallback: `${String(g.items.filter((m) => hidden.has(m.key)).length)} of ${String(g.items.length)} sections hidden`,
              })}
            </span>
          )}
          renderItem={(m: { key: string }) => (
            <label
              key={m.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "5px 10px",
                borderTop: "1px solid var(--br)",
                fontSize: FS.s11,
                cursor: busy ? "wait" : "pointer",
                opacity: hidden.has(m.key) ? 0.6 : 1,
              }}
            >
              <input type="checkbox" checked={hidden.has(m.key)} disabled={busy} onChange={() => { toggle(m.key); }} />
              <span style={{ flex: 1, color: "var(--tp)" }}>{sectionLabel(m)}</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: FS.s9, color: "var(--tm)" }}>{m.key}</span>
              <span style={{ fontSize: FS.s9_5, color: hidden.has(m.key) ? "var(--am)" : "var(--tm)" }}>
                {hidden.has(m.key) ? t("roles.hidden_state", { fallback: "hidden for the firm" }) : t("roles.visible_state", { fallback: "visible" })}
              </span>
            </label>
          )}
        />
      )}
    </div>
  );
}

function MatrixView({
  roles,
  groups,
  keyCount,
  query,
  setQuery,
  viewFilter,
  setViewFilter,
  busyKey,
  onToggle,
  onAskDuplicate,
}: any) {
  const { t } = useTranslation();
  const sectionLabel = useSectionLabel();
  // Phase 37 — subsection groups are collapsible (collapsed by default) inside
  // the dense role × key table. Phase 37 S3 correction 2 — each top-level VIEW
  // is now its OWN collapsible header row (collapsed by default) with its
  // subsections nested beneath; the view name is no longer merged into the
  // first subsection row (brings the matrix tab in line with the per-role tab).
  const [openSubs, setOpenSubs] = useState<Record<string, any>>({});
  const [openViews, setOpenViews] = useState<Record<string, any>>({});
  const toggleSub = (id: any) => setOpenSubs((p) => ({ ...p, [id]: !p[id] }));
  const toggleView = (v: any) => setOpenViews((p) => ({ ...p, [v]: !p[v] }));
  const colCount = 1 + roles.length;
  // An active search reveals matches by forcing every level open.
  const forceOpen = (query || "").trim().length > 0;
  // Order views as they first appear in the (filtered) groups, bucketing each
  // view's subsection groups so we can render a view header + its children.
  const viewsInOrder: any[] = [];
  const groupsByView: Record<string, any> = {};
  for (const g of groups) {
    if (!groupsByView[g.view]) {
      groupsByView[g.view] = [];
      viewsInOrder.push(g.view);
    }
    groupsByView[g.view].push(g);
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", flex: "0 0 220px" }}>
          <Search
            size={11}
            style={{
              position: "absolute",
              left: 7,
              top: 7,
              color: "var(--tm)",
            }}
          />
          <input
            className="field-input"
            placeholder={t("roles.matrix_filter_placeholder", {
              fallback: "Filter section by key or label…",
            })}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ paddingLeft: 22, fontSize: FS.s11 }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              style={{
                position: "absolute",
                right: 4,
                top: 4,
                background: "none",
                border: "none",
                color: "var(--tm)",
                cursor: "pointer",
              }}
            >
              <X size={11} />
            </button>
          )}
        </div>
        {VIEW_ORDER.map((v) => (
          <label
            key={v}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "3px 9px",
              borderRadius: RAD.r12,
              background: viewFilter[v] ? viewColor(v) : "transparent",
              border: "1px solid var(--br)",
              color: viewFilter[v] ? "var(--tp)" : "var(--tm)",
              fontSize: FS.s10,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            <input
              type="checkbox"
              checked={viewFilter[v]}
              onChange={(e) =>
                setViewFilter((p: any) => ({ ...p, [v]: e.target.checked }))
              }
              style={{ width: 11, height: 11, cursor: "pointer" }}
            />
            {t(`permView.${v}`, { fallback: VIEW_LABEL[v] || v })}
          </label>
        ))}
        <span style={{ fontSize: FS.s9_5, color: "var(--tm)", marginLeft: "auto" }}>
          {/* 209 §7 [RULED 16] — one sentence per number */}
          {[
            t("roles.sections_n", { count: keyCount, fallback: `${keyCount} section${keyCount === 1 ? "" : "s"}` }),
            t("roles.roles_n", { count: roles.length, fallback: `${roles.length} role${roles.length === 1 ? "" : "s"}` }),
          ].join(" · ")}
        </span>
      </div>

      <div
        style={{
          overflowX: "auto",
          border: "1px solid var(--br)",
          borderRadius: RAD.r8,
        }}
      >
        <Table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: FS.s10_5 }}
        >
          <thead>
            <tr style={{ background: "var(--bi)" }}>
              <th
                style={{
                  position: "sticky",
                  left: 0,
                  top: 0,
                  background: "var(--bi)",
                  textAlign: "left",
                  padding: "6px 8px",
                  fontSize: FS.s9_5,
                  color: "var(--tm)",
                  textTransform: "uppercase",
                  letterSpacing: ".4px",
                  fontWeight: 600,
                  minWidth: 240,
                  borderRight: "1px solid var(--br)",
                  borderBottom: "1px solid var(--br)",
                }}
              >
                {t("roles.section_col", { fallback: "Section" })}
              </th>
              {roles.map((r: any) => (
                <th
                  key={r.id}
                  style={{
                    textAlign: "center",
                    padding: "4px 8px",
                    fontSize: FS.s9_5,
                    color: r.isSystem ? CT.c_c084fc : "var(--tp)",
                    fontWeight: 600,
                    minWidth: 70,
                    borderBottom: "1px solid var(--br)",
                    whiteSpace: "nowrap",
                  }}
                  title={`${pickRoleLabel(r)}${r.isSystem ? " " + t("roles.tag_system", { fallback: "(system)" }) : ""}`}
                >
                  <div>{pickRoleLabel(r)}</div>
                  <button
                    type="button"
                    onClick={() => onAskDuplicate(r)}
                    title={t("roles.duplicate_this_role", {
                      fallback: "Duplicate this role",
                    })}
                    style={{
                      fontSize: FS.s8,
                      color: "var(--tm)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "1px 4px",
                      fontFamily: "inherit",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Copy size={8} />
                    {t("roles.dup_short", { fallback: "dup" })}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {viewsInOrder.map((view) => {
              const vGroups = groupsByView[view];
              const vItems = vGroups.flatMap((g: any) => g.items);
              const viewOpen = forceOpen || !!openViews[view];
              const vAllowed = roles.map(
                (r: any) => vItems.filter((m: any) => r.defaults?.[m.key]).length,
              );
              const rows = [];
              // Top-level VIEW header row — its own collapsible band.
              rows.push(
                <tr
                  key={`view:${view}`}
                  onClick={() => toggleView(view)}
                  style={{ cursor: "pointer" }}
                >
                  <td
                    style={{
                      position: "sticky",
                      left: 0,
                      background: viewColor(view),
                      padding: "6px 8px",
                      borderRight: "1px solid var(--br)",
                      borderTop: "2px solid var(--br)",
                      borderBottom: "1px solid var(--br)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {viewOpen ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronRight size={13} />
                      )}
                      <span
                        style={{
                          fontSize: FS.s9_5,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: ".5px",
                          color: "var(--ts)",
                        }}
                      >
                        {t(`permView.${view}`, {
                          fallback: VIEW_LABEL[view] || view,
                        })}
                      </span>
                      <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                        ({vItems.length})
                      </span>
                    </div>
                  </td>
                  {roles.map((r: any, i: any) => (
                    <td
                      key={r.id}
                      style={{
                        textAlign: "center",
                        padding: "4px",
                        background: viewColor(view),
                        borderTop: "2px solid var(--br)",
                        borderBottom: "1px solid var(--br)",
                        fontSize: FS.s9,
                        fontWeight: 700,
                        color: "var(--ts)",
                      }}
                      title={t("roles.allowed_count", withCount(vAllowed[i], "allowed", { total: vItems.length,
                        fallback: `${vAllowed[i]} / ${vItems.length} allowed`,
                      }))}
                    >
                      {vAllowed[i]}/{vItems.length}
                    </td>
                  ))}
                </tr>,
              );
              if (!viewOpen) return rows;
              for (const g of vGroups) {
                const isOpen = forceOpen || !!openSubs[g.subId];
                const allowedTotals = roles.map(
                  (r: any) => g.items.filter((m: any) => r.defaults?.[m.key]).length,
                );
                // Subsection header row — nested beneath the view header (the
                // view name is no longer merged in here).
                rows.push(
                  <tr
                    key={`h:${g.subId}`}
                    onClick={() => toggleSub(g.subId)}
                    style={{ cursor: "pointer" }}
                  >
                    <td
                      style={{
                        position: "sticky",
                        left: 0,
                        background: "var(--bi)",
                        padding: "5px 8px 5px 26px",
                        borderRight: "1px solid var(--br)",
                        borderBottom: "1px solid var(--br)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {isOpen ? (
                          <ChevronDown size={12} />
                        ) : (
                          <ChevronRight size={12} />
                        )}
                        <span style={{ fontSize: FS.s11, fontWeight: 600, color: "var(--tp)" }}>
                          {t(g.subLabelKey, { fallback: g.subLabel })}
                        </span>
                        <span style={{ fontSize: FS.s9, color: "var(--tm)" }}>
                          ({g.items.length})
                        </span>
                      </div>
                    </td>
                    {roles.map((r: any, i: any) => (
                      <td
                        key={r.id}
                        style={{
                          textAlign: "center",
                          padding: "3px 4px",
                          background: "var(--bi)",
                          borderBottom: "1px solid var(--br)",
                          fontSize: FS.s9,
                          color: "var(--tm)",
                        }}
                        title={t("roles.allowed_count", withCount(allowedTotals[i], "allowed", { total: g.items.length,
                          fallback: `${allowedTotals[i]} / ${g.items.length} allowed`,
                        }))}
                      >
                        {allowedTotals[i]}/{g.items.length}
                      </td>
                    ))}
                  </tr>,
                );
                if (isOpen) {
                  for (const m of g.items) {
                    rows.push(
                      <tr key={m.key}>
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            background: "var(--bd)",
                            padding: "4px 8px 4px 42px",
                            borderRight: "1px solid var(--br)",
                            borderBottom: "1px solid rgba(255,255,255,.04)",
                          }}
                        >
                          <div style={{ color: "var(--tp)" }}>
                            {sectionLabel(m)}
                            {/* 174 §6.2 — GOVERNED, NOT GRANTABLE. Three keys
                                (settings.personal.user, settings.users.admins,
                                settings.users.rolesPerm) appear nowhere outside
                                the registry, so a key census reads them as
                                ghosts — and they are not. All three carry
                                `isAdminOnly`, and their own descriptions say the
                                real gate is the server-side admin flag
                                (their three `description` fields in
                                `server/src/lib/sectionRegistry.js`). They
                                are enforced by a DIFFERENT MECHANISM, not
                                unenforced, and deleting them would remove the
                                only place an admin can see those capabilities
                                exist. So the matrix says which they are: a
                                switch that cannot change anything must not look
                                like one. */}
                            {m.isAdminOnly && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontSize: FS.s8_5,
                                  fontWeight: 600,
                                  color: "var(--tm)",
                                  border: "1px solid var(--br)",
                                  borderRadius: RAD.r4,
                                  padding: "0 4px",
                                }}
                                title={t("roles.admin_only_hint", {
                                  fallback:
                                    "Governed by the server-side admin flag — it cannot be granted or withheld here.",
                                })}
                              >
                                {t("roles.admin_only", { fallback: "admin only" })}
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontFamily: "'JetBrains Mono',monospace",
                              fontSize: FS.s8_5,
                              color: "var(--tm)",
                            }}
                          >
                            {m.key}
                          </div>
                        </td>
                        {roles.map((r: any) => {
                          const on = !!r.defaults?.[m.key];
                          const busy = busyKey === `${r.id}:${m.key}`;
                          /* 174 §6.2 — an admin-only row renders a READING of
                             the admin flag, never a pill: the pill's whole
                             affordance is "click me and this role changes", and
                             for these three keys nothing changes. */
                          if (m.isAdminOnly) {
                            return (
                              <td
                                key={r.id}
                                style={{
                                  textAlign: "center",
                                  padding: "3px 4px",
                                  borderBottom: "1px solid rgba(255,255,255,.04)",
                                  fontSize: FS.s8_5,
                                  color: "var(--tm)",
                                }}
                                title={t("roles.admin_only_hint", {
                                  fallback:
                                    "Governed by the server-side admin flag — it cannot be granted or withheld here.",
                                })}
                              >
                                {t("roles.admin_only_cell", { fallback: "admin" })}
                              </td>
                            );
                          }
                          return (
                            <td
                              key={r.id}
                              style={{
                                textAlign: "center",
                                padding: "3px 4px",
                                borderBottom: "1px solid rgba(255,255,255,.04)",
                                opacity: busy ? 0.5 : 1,
                              }}
                            >
                              <PermPill
                                on={on}
                                disabled={busy}
                                onClick={() => onToggle(r, m.key)}
                              />
                            </td>
                          );
                        })}
                      </tr>,
                    );
                  }
                }
              }
              return rows;
            })}
            {groups.length === 0 && (
              <tr>
                <td
                  colSpan={colCount}
                  style={{
                    padding: 12,
                    textAlign: "center",
                    color: "var(--tm)",
                    fontSize: FS.s11,
                  }}
                >
                  {t("roles.no_sections_match", {
                    fallback: "No sections match the current filter.",
                  })}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function DuplicateModal({ source, existingNames, onCancel, onConfirm }: { source?: any; existingNames?: any; onCancel?: any; onConfirm?: any }) {
  const { t } = useTranslation();
  const [name, setName] = useState(source.name + "Custom");
  const [label, setLabel] = useState(
    (source.displayLabel || source.name) +
      " " +
      t("roles.custom_suffix", { fallback: "(custom)" }),
  );
  const collision = existingNames.includes(name);
  const valid =
    name.trim().length > 0 &&
    /^[A-Za-z][A-Za-z0-9_]*$/.test(name) &&
    !collision &&
    label.trim().length > 0;

  return (
    <ModalLayout
      onClose={onCancel}
      maxWidth={460}
      headerContent={
        <div className="modal-header">
          <h3>
            {t("roles.duplicate_modal_title", {
              name: source.displayLabel || source.name,
              fallback: `Duplicate "${source.displayLabel || source.name}"`,
            })}
          </h3>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancel}
            style={{ padding: "2px 6px" }}
          >
            <X size={11} />
          </button>
        </div>
      }
      footer={
        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onCancel}>
            {t("common.cancel", { fallback: "Cancel" })}
          </button>
          <button
            type="button"
            className="modal-save"
            disabled={!valid}
            onClick={() => onConfirm(name, label)}
            style={{
              opacity: valid ? 1 : 0.5,
              cursor: valid ? "pointer" : "not-allowed",
            }}
          >
            {t("roles.create_custom_role", { fallback: "Create custom role" })}
          </button>
        </div>
      }
    >
          <p
            style={{
              fontSize: FS.s10_5,
              color: "var(--tm)",
              margin: "0 0 10px",
              lineHeight: 1.5,
            }}
          >
            {t("roles.duplicate_modal_help", {
              fallback:
                "Creates a new custom role with the same defaults, ratio, and visibility. The new role is editable and deletable (not a system preset).",
            })}
          </p>
          <div className="field">
            <label>
              {t("roles.internal_name_required", {
                fallback: "Internal name *",
              })}
            </label>
            <input autoComplete="off" /* 207 §18 */
              className="field-input no-contact-autofill"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder={t("roles.internal_name_placeholder", {
                fallback: "e.g. SeniorPartner",
              })}
              style={{ fontFamily: "'JetBrains Mono',monospace" }}
            />
            {!/^[A-Za-z][A-Za-z0-9_]*$/.test(name) && (
              <p
                style={{ fontSize: FS.s9_5, color: "var(--am)", margin: "3px 0 0" }}
              >
                {t("roles.internal_name_format", {
                  fallback:
                    "Must start with a letter and contain only letters, digits, and underscores.",
                })}
              </p>
            )}
            {collision && (
              <p
                style={{ fontSize: FS.s9_5, color: "var(--rd)", margin: "3px 0 0" }}
              >
                {t("roles.name_collision", {
                  fallback: "A role with this name already exists.",
                })}
              </p>
            )}
          </div>
          <div className="field">
            <label>
              {t("roles.display_label_required", {
                fallback: "Display label *",
              })}
            </label>
            <input
              className="field-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
    </ModalLayout>
  );
}
